import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { formatSI, formatSigned } from './format'

export type DeltaTone = 'good' | 'bad' | 'neutral'

const TONE_CLASS: Record<DeltaTone, string> = {
  good: 'text-ok',
  bad: 'text-critical',
  neutral: 'text-ink-2',
}

export interface MetricDelta {
  /** Raw signed change, e.g. -1 or 0.02. */
  value: number
  /** Human suffix, e.g. "vs 1h" or "vs yesterday". */
  label?: string
  /** Whether this change is good, bad, or neutral — increases aren't always good. */
  tone: DeltaTone
  /** Override the auto-formatted delta text (e.g. "+2pp"). */
  text?: string
}

export function MetricNumber({
  value,
  unit,
  delta,
  si = true,
  precision = 1,
  className,
}: {
  value: number | string
  unit?: string
  delta?: MetricDelta
  si?: boolean
  precision?: number
  className?: string
}) {
  const display = typeof value === 'number' ? (si ? formatSI(value, precision) : value.toFixed(precision)) : value
  const full = typeof value === 'number' ? value.toLocaleString() : value

  return (
    <div className={className}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="mono flex cursor-help items-baseline gap-1 text-[21px] font-semibold">
            {display}
            {unit && <span className="text-[11px] font-medium text-ink-2">{unit}</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {full}
          {unit ? ` ${unit}` : ''}
        </TooltipContent>
      </Tooltip>
      {delta && (
        <div className={cn('mono mt-0.5 text-[11px]', TONE_CLASS[delta.tone])}>
          {delta.text ?? formatSigned(delta.value, precision)} {delta.label}
        </div>
      )}
    </div>
  )
}
